import React, { useState, useCallback } from "react";
import { Box, Group, useMantineTheme } from "@mantine/core";

const OtpInput = React.forwardRef(
  ({ value = "", onChange, onComplete, error, length = 6, size = "xl" }, ref) => {
    const theme = useMantineTheme();
    const [isFocused, setIsFocused] = useState(false);

    const isXl = size === "xl";
    const boxSize = isXl ? 44 : 38;
    const fontSize = isXl ? 22 : 18;

    const handleChange = useCallback(
      (e) => {
        const raw = e.target.value.replace(/\D/g, "").slice(0, length);
        onChange(raw);
        if (raw.length === length) {
          // Fire onComplete synchronously and pass the raw value so
          // the consumer can submit on the same tick as the keystroke
          // without waiting for React to commit the onChange state.
          onComplete?.(raw);
        }
      },
      [onChange, length, onComplete]
    );

    const handleFocus = useCallback(() => setIsFocused(true), []);
    const handleBlur = useCallback(() => setIsFocused(false), []);
    const handleClick = useCallback(() => {
      if (ref && ref.current) {
        ref.current.focus();
      }
    }, [ref]);

    const isDark = theme.colorScheme === "dark";
    const primaryColor = theme.colors[theme.primaryColor][6];
    const borderColor = isDark ? theme.colors.dark[4] : theme.colors.gray[4];
    const bgColor = isDark ? theme.colors.dark[6] : theme.white;
    const textColor = isDark ? theme.colors.gray[0] : theme.black;
    const errorColor = theme.colors.red[6];

    return (
      <Box
        style={{ position: "relative", display: "inline-block" }}
        onClick={handleClick}
      >
        <style>{`@keyframes otpBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }`}</style>
        {/* Real input for iOS SMS autofill and keyboard */}
        <input
          ref={ref}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          autoFocus
          maxLength={length}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            position: "absolute",
            top: 0,
            insetInlineStart: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            zIndex: 2,
            fontSize: "16px",
            caretColor: "transparent",
            border: "none",
            background: "transparent",
          }}
        />
        {/* Visual digit boxes */}
        <Group spacing={8} position="center" style={{ zIndex: 1 }}>
          {Array.from({ length }).map((_, i) => {
            const isActive = i === value.length && isFocused;
            const isFilled = i < value.length;
            const borderClr = error
              ? errorColor
              : isActive
              ? primaryColor
              : isFilled
              ? primaryColor
              : borderColor;

            return (
              <Box
                key={i}
                style={{
                  position: "relative",
                  width: boxSize,
                  height: boxSize,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize,
                  fontWeight: 600,
                  color: textColor,
                  backgroundColor: bgColor,
                  border: `2px solid ${borderClr}`,
                  borderRadius: theme.radius.sm,
                  transition: "border-color 150ms ease",
                }}
              >
                {value[i] || ""}
                {isActive && (
                  <Box
                    style={{
                      position: "absolute",
                      width: 2,
                      height: fontSize + 4,
                      backgroundColor: primaryColor,
                      animation: "otpBlink 1s step-end infinite",
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Group>
      </Box>
    );
  }
);

OtpInput.displayName = "OtpInput";

export default OtpInput;
